import { Box, Typography } from '@mui/material';
import { TextField, type TextFieldProps } from './text-field';
import { useState, useEffect } from 'react';

interface TextAreaProps extends Omit<TextFieldProps, 'maxLength'> {
    maxLength?: number;
    showCharacterCount?: boolean;
    /** Show characters remaining (e.g. "12 characters remaining") instead of "used/max". */
    showRemaining?: boolean;
}

export const TextArea = ({
    maxLength,
    showCharacterCount = true,
    showRemaining = false,
    value,
    onChange,
    helperText,
    minRows = 2,
    maxRows = 5,
    ...props
}: TextAreaProps) => {
    const [characterCount, setCharacterCount] = useState(0);

    useEffect(() => {
        const count = typeof value === 'string' ? value.length : 0;
        setCharacterCount(count);
    }, [value]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        // If maxLength is set, prevent exceeding it
        if (maxLength && newValue.length > maxLength) {
            return;
        }

        if (onChange) {
            onChange(event);
        }
    };

    const renderCharacterCount = () => {
        if (!showCharacterCount || !maxLength) return null;

        const isOverLimit = maxLength && characterCount > maxLength;
        const color = isOverLimit ? 'error.main' : 'text.secondary';

        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.1 }}>
                <Typography variant="caption" color={color} sx={{ fontSize: '0.75rem' }}>
                    {showRemaining
                        ? `${maxLength - characterCount} characters remaining`
                        : `${characterCount}${maxLength ? `/${maxLength}` : ''} characters`}
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ marginBottom: 1 }}>
            <TextField
                multiline
                {...props}
                noMargin={!!showCharacterCount && !!maxLength ? true : props.noMargin}
                minRows={minRows}
                maxRows={maxRows}
                value={value}
                onChange={handleChange}
                helperText={helperText}
                maxLength={maxLength}
                slotProps={{
                    ...props.slotProps
                }}
            />
            {renderCharacterCount()}
        </Box>
    );
};
